export function hasFacility(marina, keywords = []) {
  const facilities = (marina?.facilities || []).map((item) => String(item).toLowerCase());
  return keywords.some((keyword) => facilities.some((facility) => facility.includes(keyword)));
}

export function buildMarinaIntelligence(marina) {
  const currency = marina.currency || "EUR";
  const nightly = Number(marina.price_per_night || 85);
  const sheltered = !!marina.sheltered_from_wind;
  const draft = marina.max_draft_m || 3.2;
  const loa = marina.max_vessel_length_m || 18;
  const berthCount = marina.total_berths || 120;
  const available = marina.available_berths ?? Math.max(4, Math.round(berthCount * 0.16));

  return {
    pricing: [
      { label: "Overnight berth", low: nightly, high: Math.round(nightly * 1.45), note: "High season varies by LOA" },
      { label: "Weekly stay", low: Math.round(nightly * 5.7), high: Math.round(nightly * 8.2), note: "Indicative visitor pricing" },
      { label: "Monthly berth", low: Math.round(nightly * 21), high: Math.round(nightly * 32), note: "Ask marina for contract rates" },
      { label: "Electricity / water", low: 8, high: 28, note: "Metered or daily service fee" },
    ].map((item) => ({ ...item, currency })),
    facts: [
      { label: "Berths", value: berthCount },
      { label: "Available", value: available, tone: available > 10 ? "good" : "warn" },
      { label: "Max LOA", value: `${loa}m` },
      { label: "Max Draft", value: `${draft}m` },
      { label: "Fuel", value: hasFacility(marina, ["fuel"]) ? "Available" : "Confirm" },
      { label: "Electricity", value: hasFacility(marina, ["electric"]) ? "Yes" : "Likely" },
      { label: "Water", value: hasFacility(marina, ["water"]) ? "Yes" : "Likely" },
      { label: "WiFi", value: hasFacility(marina, ["wifi", "wi-fi"]) ? "Yes" : "Ask office" },
      { label: "Showers", value: hasFacility(marina, ["shower"]) ? "Yes" : "Confirm" },
      { label: "Repairs", value: hasFacility(marina, ["repair"]) ? "On site" : "Nearby" },
      { label: "Customs", value: marina.superyacht_friendly ? "Supported" : "Check port" },
      { label: "Security", value: marina.superyacht_friendly ? "High" : "Standard" },
    ],
    approach: [
      `Approach ${marina.name} using marked water and confirm the latest local notices before arrival.`,
      `Maintain a conservative entry speed below 5 knots near the breakwater and guest berths.`,
      `Recommended entry is in daylight for first-time visitors, especially with crosswind or swell.`,
      `Entrance depth planning target: ${Math.max(2.2, draft - 0.4).toFixed(1)}m+ with updated chart confirmation.`,
    ],
    hazards: [
      { title: "Shallow water outside channel", severity: "warning", text: "Keep to marked approaches and avoid cutting corners close to the entrance." },
      { title: "Crosswind during docking", severity: sheltered ? "caution" : "warning", text: sheltered ? "Generally protected, but gusts may funnel near quay ends." : "Exposed wind can complicate stern-to or alongside approaches." },
      { title: "Local traffic", severity: "caution", text: "Expect tenders, ferries, fishing vessels, and charter turnovers during peak hours." },
    ],
    rules: ["Contact marina office before entry", "Observe no-wake speed limits", "Use holding tank and waste disposal points", "Keep VHF watch on arrival", "Avoid anchoring inside maneuvering fairways", "Confirm check-in and visitor berth assignment"],
    exposure: {
      protection: sheltered ? 88 : 68,
      swell: sheltered ? "Low to moderate" : "Moderate in exposed conditions",
      best: sheltered ? "North / west protection generally strong" : "Best in settled or offshore wind conditions",
      dangerous: sheltered ? "Strong easterlies may still create surge" : "Strong onshore winds and southern swell require caution",
      seasonal: marina.best_season === "all_year" ? "Useful year-round with standard storm checks" : "Best used in settled summer cruising windows",
    },
    smartInsights: [
      available > 10 ? "Berth availability looks favorable, but call ahead during charter turnover days." : "Limited berth availability: contact the marina before committing to arrival.",
      sheltered ? "Good overnight candidate when exposed anchorages are uncomfortable." : "Review wind direction carefully before using this marina as a weather shelter.",
      `For safest arrival, plan a daylight approach and keep ${marina.vhf_channel ? `VHF ${marina.vhf_channel}` : "marina VHF"} ready before the entrance.`,
    ],
  };
}